import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, RefreshCw, Download, LogOut, ChevronDown, Eye, X } from 'lucide-react';
import { useCreatorPortal } from '../../contexts/CreatorPortalContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { HubSwitcher } from '../HubSwitcher';
import { exportCreatorCampaignsCSV, downloadCSV } from '../../services/creatorPortalService';

export function CreatorsHubHeader() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const {
    influencer, campaigns, isRealtimeConnected, realtimeError,
    lastSyncedAt, refreshing, refresh, signOut, viewingAs
  } = useCreatorPortal();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleExport = () => {
    const csv = exportCreatorCampaignsCSV(campaigns);
    const date = new Date().toISOString().split('T')[0];
    downloadCSV(csv, `campaigns-${date}.csv`);
  };

  const formatSyncTime = () => {
    if (!lastSyncedAt) return '';
    const now = new Date();
    const diff = Math.floor((now.getTime() - lastSyncedAt.getTime()) / 1000);
    if (diff < 60) return t('creatorsHub.justNow');
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return lastSyncedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const initials = influencer?.full_name
    ?.split(' ')
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-brand-ink rounded-xl flex items-center justify-center shadow-sm">
                <Sparkles className="w-5 h-5 text-brand-lime" />
              </div>
              <h1 className="mo-display text-base text-brand-ink hidden sm:block">{t('creatorsHub.title')}</h1>
            </div>

            <div className="hidden md:flex items-center gap-2">
              {isRealtimeConnected ? (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 rounded-full border border-emerald-100">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                  <span className="text-xs font-medium text-emerald-700">{t('creatorsHub.live')}</span>
                </div>
              ) : realtimeError ? (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 rounded-full border border-amber-100">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                  <span className="text-xs font-medium text-amber-700">{t('creatorsHub.offline')}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-full border border-gray-200">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" />
                  <span className="text-xs font-medium text-gray-500">{t('creatorsHub.connecting')}</span>
                </div>
              )}
              {lastSyncedAt && (
                <span className="text-xs text-gray-400">{formatSyncTime()}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {viewingAs && (
              <div className="flex items-center gap-2 pl-3 pr-1.5 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
                <Eye className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-xs font-semibold text-amber-700 max-w-[140px] truncate">
                  {t('creatorsHub.viewingAs')} {influencer?.full_name}
                </span>
                <button
                  onClick={() => navigate('/apps?creators=1')}
                  className="p-1 rounded-full text-amber-500 hover:text-amber-700 hover:bg-amber-100 transition-colors"
                  title={t('creatorsHub.viewingAsExit')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <HubSwitcher current="creators" className="hidden sm:block" />

            <button
              onClick={handleExport}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('creatorsHub.exportData')}
            >
              <Download className="w-4 h-4" />
              <span className="hidden lg:inline">{t('creatorsHub.exportData')}</span>
            </button>

            <button
              onClick={refresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title={t('creatorsHub.refresh')}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{t('creatorsHub.refresh')}</span>
            </button>

            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {initials}
                </div>
                <span className="hidden md:block text-sm font-medium text-gray-700 max-w-[120px] truncate">
                  {influencer?.full_name}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 hidden md:block" />
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 shadow-sm border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">{influencer?.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">{influencer?.email}</p>
                  </div>
                  <button
                    onClick={() => { setMenuOpen(false); handleExport(); }}
                    className="sm:hidden w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    {t('creatorsHub.exportData')}
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); signOut(); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('creatorsHub.signOut')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
